// Get priority transactions with client and teams params

  // Update a team notification

  // Delete a team notification



 

import { createContext, useEffect, useReducer, ReactElement, useState, useRef, useCallback } from 'react';

// third-party
import { Chance } from 'chance';
// reducer - state management
import { LOGIN, LOGOUT, OTHER } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project-imports
import Loader from 'components/Loader';
import axios, { onSignedOutPage } from 'utils/axios';
import axiosBase from 'axios';
import type { WireImportMapping } from 'utils/importMapping';
import {
  AuthProps,
  ApiContextType,
  LoginType,
  CallStatus,
  AnnouncementListParams,
  CreateAnnouncementPayload,
  UpdateAnnouncementPayload
} from 'types/auth';
import {
  ConsentReportParams,
  LegalConsentSettings,
  LegalSlug,
  LoginConsents,
  PublishLegalDocumentPayload,
  RecordSmsConsentPayload,
  consentRequiredFromResponse
} from 'types/legal';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { anchorOrigin } from 'types/snackbar';
import { markActivity, useIdleSignOut } from 'hooks/useIdleSignOut';
import { useThemeConfigContext } from 'contexts/ThemeConfigContext';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  SESSION_PLATFORM,
  SessionEndReason,
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  formatDuration,
  announceSignOut,
  clearSessionData,
  clearStoredUserData,
  dropLegacyRefreshToken,
  getRefreshToken,
  getStoredTokens,
  hasSession,
  listenToPeers,
  msUntilExpiry,
  requestTokensFromPeers,
  setSessionRefresher,
  shareTokens,
  storeTokens,
  withRefreshLock
} from 'utils/session';


// Microsoft OAuth utilities
import { completeMicrosoftOAuthFlow, getCurrentMicrosoftAccount, isMicrosoftLoggedIn } from 'utils/microsoftAuth';
import { setFirebaseToken } from 'utils/firebaseTokenBus';
 

const chance = new Chance();

// The server issues 15-minute access tokens; renew this long before one expires.
const REFRESH_AHEAD_MS = 2 * 60 * 1000;
// A refresh that failed for a network/server reason is retried this soon.
const REFRESH_RETRY_MS = 30 * 1000;
const IDLE_WARNING_KEY = 'idle-sign-out-warning';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isLoginInitiated: false,
  isInitialized: false,
  user: null
};

// Session calls go through a bare axios (no interceptors): a 401 here means the session
// is over, which the caller handles — it must not trigger the interceptor's own refresh.
// The timeout matters: the refresh runs while holding the lock every tab waits on.
const refreshTokenApi: (st: string) => Promise<any> = async (refreshToken) => {
  const response = await axiosBase.get('account/refresh', {
    baseURL: axios.defaults.baseURL,
    timeout: 15000,
    params: { platform: SESSION_PLATFORM },
    headers: {
      Authorization: `Bearer ${refreshToken}`
    }
  });

  return response.data.data;
};

// Ends the session server-side. Authenticated by the refresh token, so it still works
// after the 15-minute access token has lapsed (e.g. on the idle sign-out).
const endSessionApi = async (refreshToken: string) => {
  await axiosBase.post('account/session/end', {}, {
    baseURL: axios.defaults.baseURL,
    timeout: 4000,
    headers: {
      Authorization: `Bearer ${refreshToken}`
    }
  });
};

// ok: new tokens from the server; adopted: fresh tokens another tab just obtained;
// none: this tab holds no session; ended: the server refused the session; retry:
// network/server trouble, worth trying again.
type RefreshOutcome =
  | { status: 'ok'; data: any }
  | { status: 'adopted'; token: string }
  | { status: 'none' }
  | { status: 'ended' }
  | { status: 'retry' };

  const getPriorityTransactions = async (payload: any) => {
    const response = await axios.post('authkey/priorityTransaction', payload);
    return response.data;
  };

const setSession = (token?: string | null) => {
  if (token) {
    sessionStorage.setItem('token', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const ApiContext = createContext<ApiContextType | null>(null);

export const ApiProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const timeoutRef = useRef<number | undefined>();
  const { themeConfig } = useThemeConfigContext();

  // Global chat threads for current user (cross-transaction). Memoized —
  // and declared here, ahead of the `!isInitialized` early return below —
  // because consumers (e.g. FloatingChatWidget) list this in a
  // useCallback/useEffect dependency array. Every other function on this
  // context is a fresh closure on every ApiProvider render; without a stable
  // reference here, those effects re-fire (and re-hit the network) on every
  // unrelated provider re-render. Must stay unconditional — a hook declared
  // after the early return would be skipped on the first render and throw
  // "Rendered more hooks than during the previous render" once isInitialized
  // flips to true.
  const getUserChatThreads = useCallback(async (page: number = 1, limit: number = 10) => {
    const response = await axios.get('chat/user/threads', { params: { page, limit } });
    return response.data;
  }, []);

  // Whether the left-nav Announcements tab should render, plus the unread badge count.
  // Memoized and declared up here for the same two reasons as getUserChatThreads above:
  // AnnouncementsContext lists it in an effect dependency array, and a hook after the
  // early return would be skipped on the first render. Returns
  // { visible, count, isSuperAdmin, unreadCount, lastSeenAt } — count is null for a
  // Super Admin, who always sees the tab and never has an unread count.
  const getAnnouncementVisibility = useCallback(async () => {
    const response = await axios.get('announcement/visibility');
    return response.data;
  }, []);

  // Opening one announcement clears one from the badge. Returns { uid, unreadCount } —
  // the recounted total, not an ack, so the caller never has to decrement locally and
  // drift. Idempotent: re-reading the same announcement returns the same count.
  // Memoized for the same reason as the two above — AnnouncementsContext lists it in a
  // useCallback dependency array.
  const markAnnouncementRead = useCallback(async (uid: string) => {
    const response = await axios.post(`announcement/uid/${uid}/read`);
    return response.data;
  }, []);

  // The escape hatch behind "Mark all as read" — clears the badge without making the
  // user open every announcement. Returns { unreadCount: 0, lastSeenAt }.
  const markAllAnnouncementsRead = useCallback(async () => {
    const response = await axios.post('announcement/read-all');
    return response.data;
  }, []);

  // Returns { isSuperAdmin } — decided server-side (`!role.client`), never by role name.
  // Gates the Legal & Consent tab and the default-documents editor; the legal admin
  // routes enforce it again. Memoized for the same reason as the functions above.
  const getLegalAccess = useCallback(async () => {
    const response = await axios.get('legal/access');
    return response.data;
  }, []);

  // ------------------------------------------------------------------ session
  // Short server-tracked sessions (VAPT "Improper Session Expiration"): 15-minute
  // access tokens renewed ahead of expiry, a single-use 30-minute refresh token, a
  // 12-hour cap, and a 15-minute idle sign-out. See utils/session for storage and the
  // cross-tab hand-off.

  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  const clearRefreshTimer = () => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  };

  // Renew the access token shortly before it expires.
  const scheduleRefresh = (token: string) => {
    clearRefreshTimer();
    const delay = Math.max(msUntilExpiry(token) - REFRESH_AHEAD_MS, 5000);
    timeoutRef.current = window.setTimeout(() => void refreshSession(), delay);
  };

  // notistack's closeSnackbar only exists once its provider has mounted, which it has
  // not while this provider is still initialising (it renders <Loader /> until then).
  const closeIdleWarning = () => {
    try {
      closeSnackbar(IDLE_WARNING_KEY);
    } catch {
      // No provider yet, so no warning on screen.
    }
  };

  // Sign this browser out: end the session server-side, wipe everything tied to the
  // user, and (unless another tab told us) tell the other tabs.
  const endBrowserSession = async (reason: SessionEndReason, { notifyServer = true, announce = true } = {}) => {
    clearRefreshTimer();
    closeIdleWarning();
    const refreshToken = getRefreshToken();
    if (notifyServer && refreshToken) {
      try {
        await endSessionApi(refreshToken);
      } catch (err) {
        // Unreachable server: the session still dies within 30 minutes (refresh-token lifetime).
        console.error('Ending the session on the server failed:', err);
      }
    }
    delete axios.defaults.headers.common.Authorization;
    setFirebaseToken(null);
    await clearSessionData(reason);
    if (announce) announceSignOut(reason);
  };

  // The server refused the session (signed out elsewhere, reused token, 12-hour cap).
  const expireSession = async () => {
    await endBrowserSession('expired', { notifyServer: false });
    dispatch({ type: LOGOUT });
    if (!onSignedOutPage()) window.location.pathname = '/login';
  };

  // One refresh at a time across every tab of this browser. Newer tokens another tab
  // obtained (while this one was asleep, discarded or reloading) are adopted first:
  // the refresh token is single-use, and presenting one another tab already replaced
  // makes the server end the session for every tab. With `adoptFresh`, a session
  // another tab renewed meanwhile — its reply here, or a share that arrived while this
  // tab waited for the lock — is used as it is instead of rotating again. `replacing`
  // is the access token this tab set out to renew.
  const performRefresh = ({ adoptFresh, replacing }: { adoptFresh: boolean; replacing?: string }): Promise<RefreshOutcome> =>
    withRefreshLock(async () => {
      const fromPeer = await requestTokensFromPeers();
      if (fromPeer) storeTokens(fromPeer);
      const current = getStoredTokens();
      if (!current) return { status: 'none' };
      if (adoptFresh && current.token !== replacing && msUntilExpiry(current.token) > REFRESH_AHEAD_MS) {
        return { status: 'adopted', token: current.token };
      }
      try {
        return { status: 'ok', data: await refreshTokenApi(current.refreshToken) };
      } catch (err: any) {
        return err?.response?.status === 401 ? { status: 'ended' } : { status: 'retry' };
      }
    });

  // Renew the access token; resolves with the new one, or null when there is none.
  const refreshSession = (): Promise<string | null> => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const run = performRefresh({ adoptFresh: true, replacing: getStoredTokens()?.token })
      .then(async (outcome) => {
        if (outcome.status === 'ok') {
          setLoginDetails(outcome.data);
          return outcome.data.token as string;
        }
        if (outcome.status === 'adopted') {
          setSession(outcome.token);
          scheduleRefresh(outcome.token);
          return outcome.token;
        }
        if (outcome.status === 'none') return null;
        if (outcome.status === 'ended') {
          await expireSession();
          return null;
        }
        // Network or server trouble: keep the current tokens and try again shortly.
        clearRefreshTimer();
        timeoutRef.current = window.setTimeout(() => void refreshSession(), REFRESH_RETRY_MS);
        return null;
      })
      .finally(() => {
        refreshInFlight.current = null;
      });
    refreshInFlight.current = run;
    return run;
  };

  const showIdleWarning = () => {
    showToast(`You'll be signed out in ${formatDuration(IDLE_WARNING_MS)} because you've been inactive.`, 'warning', {
      key: IDLE_WARNING_KEY,
      preventDuplicate: true,
      autoHideDuration: null,
      action: (key: string | number) => (
        <>
          <Button
            size="small"
            onClick={() => {
              markActivity();
              closeSnackbar(key);
            }}
            sx={{ color: 'white', fontWeight: 600 }}
          >
            Stay signed in
          </Button>
          <IconButton size="small" onClick={() => closeSnackbar(key)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </>
      )
    });
  };

  // Activity in any tab keeps every tab signed in; a sign-out in one signs out all.
  useIdleSignOut({
    timeoutMs: IDLE_TIMEOUT_MS,
    warningMs: IDLE_WARNING_MS,
    isSignedIn: hasSession,
    onWarning: showIdleWarning,
    onActiveAgain: closeIdleWarning,
    onIdle: () => void logout('idle')
  });

  const init = async () => {
    // The year-long refresh token older builds kept in localStorage is never used again.
    dropLegacyRefreshToken();

    let tokens = getStoredTokens();
    if (!tokens) {
      // A new tab has no tokens of its own; an open tab can hand over its session.
      const shared = await requestTokensFromPeers();
      if (shared) {
        storeTokens(shared);
        tokens = shared;
      }
    }
    if (!tokens) {
      // Signed out everywhere: drop user data a previous session (or an older build) left
      // behind. sessionStorage is kept — it carries why the last session ended. A
      // leftover Firebase sign-in is cleared by FirebaseContext once it mounts.
      clearStoredUserData();
      dispatch({ type: LOGOUT });
      return;
    }
    setSession(tokens.token);

    // Refreshing validates the session and loads the user (so it always rotates, even
    // with fresh tokens from a peer). A network blip is retried twice before giving up.
    let outcome: RefreshOutcome = { status: 'retry' };
    for (let attempt = 0; attempt < 3 && outcome.status === 'retry'; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, attempt * 1500));
      outcome = await performRefresh({ adoptFresh: false });
    }
    if (outcome.status === 'ok') {
      setLoginDetails(outcome.data);
      return;
    }
    console.error('Authentication initialization failed:', outcome.status);
    if (outcome.status === 'ended') {
      // The server refused the session — it is over for every tab.
      await endBrowserSession('expired', { notifyServer: false });
    } else {
      // Could not reach the server: sign this tab out without touching the others.
      await endBrowserSession('logout', { notifyServer: false, announce: false });
    }
    dispatch({ type: LOGOUT });
  };

  useEffect(() => {
    // Restore session token if it exists
    const existingToken = getStoredTokens()?.token;
    if (existingToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
    }

    // Lets the axios interceptor renew an expired access token and retry.
    setSessionRefresher(refreshSession);

    const stopListening = listenToPeers({
      // Another tab refreshed: use its tokens and reschedule from them.
      onTokens: (tokens) => {
        storeTokens(tokens);
        setSession(tokens.token);
        scheduleRefresh(tokens.token);
      },
      // Another tab signed out (or idled out, or its session expired).
      onSignedOut: async (reason) => {
        if (!getStoredTokens()) return;
        await endBrowserSession(reason, { notifyServer: false, announce: false });
        dispatch({ type: LOGOUT });
        window.location.reload();
      }
    });

    init();
    return () => {
      stopListening();
      setSessionRefresher(null);
      clearRefreshTimer();
    };
  }, []);

  const base64Encode = (str: string) => {
    return btoa(str);
  };

  const showToast = (
    message: string,
    variant: 'default' | 'error' | 'success' | 'warning' | 'info' = 'default',
    config: any = {},
    anchorOptions: any = {}
  ) => {
    enqueueSnackbar(message, {
      ...{
        variant,
        ...config,
        anchorOrigin: {
          ...anchorOrigin,
          ...anchorOptions
        }
      }
    });
  };

  const base64Decode = (str: string) => {
    return atob(str);
  };

  // Store and share a fresh token pair, then schedule its renewal.
  const setLoginDetails = (resp: { refreshToken: string; accessToken: string; token: string; user: any; firebaseToken?: string }) => {
    let { token, refreshToken, user, firebaseToken } = resp;
    const tokens = { token, refreshToken, obtainedAt: Date.now() };
    storeTokens(tokens);
    shareTokens(tokens);
    setSession(token);
    setFirebaseToken(firebaseToken ?? null);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        isLoginInitiated: true,
        user
      }
    });
    scheduleRefresh(token);
  };

  // `consents` carries only the login-page boxes that were shown and ticked. A 428
  // CONSENT_REQUIRED is rethrown as a ConsentRequiredError (see types/legal).
  const login = async (email: string, password: string, consents?: LoginConsents, strategy: LoginType = LoginType.USER_NAME) => {
    let payload = {
      strategy,
      payload: {
        username: base64Encode(email),
        password: base64Encode(password),
        platform: 'DASHBOARD',
        ...(consents && Object.keys(consents).length ? { consents } : {})
      }
    };
    let response;
    try {
      response = await axios.post('account/login', payload);
    } catch (error: any) {
      throw consentRequiredFromResponse(error) ?? error;
    }
    let { success, data } = response.data;

    if (success) {
      setLoginDetails(data);
    } else {
      dispatch({
        type: OTHER,
        payload: {
          isLoginInitiated: true,
          isLoggedIn: false
        }
      });
    }
  };

    const updateTeamNotification = async (teamUid: string, notificationUid: string, payload: any) => {
    const response = await axios.put(`/team/uid/${teamUid}/notifications/${notificationUid}`, payload);
    return response.data;
  };

  async function updateNotificationStatus(id:string, status:boolean) {
  const res = await axios.put('/authkey/notification-status', { id, status });
  return res.data;
}

  const deleteTeamNotification = async (teamUid: string, notificationId: string) => {
    const response = await axios.delete(`/team/uid/${teamUid}/notifications/${notificationId}`);
    return response.data;
  };

  // Mint a signed URL for an already-uploaded object. The `url` returned at upload time
  // lives only 600s, and the one embedded in a read response can fail to sign or go stale
  // in a long-open tab — this re-mints from the stored file_path on demand.
  // Memoized for the reason documented on getUserChatThreads above: consumers list it in
  // effect dependency arrays, and a fresh closure each render would re-fire the request.
  const getAssetSignedUrl = useCallback(async (file_path: string, file_name?: string) => {
    const response = await axios.post('utility/file/signed', { file_path, file_name });
    return response.data;
  }, []);

  const uploadAsset = async (file: File, path: string) => {
    var payload = new FormData();
    payload.append('file', file);
    payload.append('path', path);
    const response = await axios.post('utility/file/upload', payload);
    return response.data;
  };

    
  const saveTeamNotifications = async (teamUid: string, notifications: any) => {
    const response = await axios.post(`/team/uid/${teamUid}/notifications`, notifications);
    return response.data;
  };


  

  const createClient = async (payload: any) => {
    let email = base64Encode(payload.email);
    let phone = base64Encode(payload.phone);
    payload = { ...payload, email, phone }
    const response = await axios.post('client', payload);
    return response.data;
  };

  const createTeam = async (payload: any) => {
    const response = await axios.post('team', payload);
    return response.data;
  };

  const updateClient = async (payload: any, uid: string) => {
    let email = base64Encode(payload.email);
    let phone = base64Encode(payload.phone);
    payload = { ...payload, email, phone };
    const response = await axios.put(`client/manage/${uid}`, payload);
    return response.data;
  };

  const getFormData = async (key: string) => {
    const response = await axios.get(`authkey/details/${key}`);
    return response.data;
  };
  const getTeamByClient = async (uid: string) => {
    // console.log("uid client",uid)
    const response = await axios.get(`team/client/${uid}/teams`);
    return response.data;
  };

  //  Get Team Details
  const getTeamDetailsByUid = async (uid: string) => {
    const response = await axios.get(`team/uid/${uid}/details`);
    return response.data;
  };

  const updateTeam = async (payload: any, uid: string) => {
    const response = await axios.put(`team/manage/${uid}`, payload);
    return response.data;
  };

  const updateChildTxnStartStep = async (uid: string, childTxnStartStep: string | null) => {
    const response = await axios.put(`team/manage/${uid}/child-start-step`, { childTxnStartStep });
    return response.data;
  };

  const getClients = async (payload: { page: number; size: number; keyword?: string; status?: 'ACTIVE' | 'INACTIVE' }) => {
    const response = await axios.get('client', { params: payload });
    return response.data;
  };

  const mapKeyToTeam = async (payload: any) => {
    const response = await axios.put(`authkey/map`, payload);
    return response.data;
  };

  const submitSSODetails = async (payload: any, uid: string) => {
    const response = await axios.put(`client/uid/${uid}/config`, payload);
    return response.data;
  };

  const getSSODetails = async (uid: string) => {
    const response = await axios.get(`client/uid/${uid}/config`);
    return response.data;
  };

  const getTransactionHistoryDetails = async (uid: string) => {
    const response = await axios.get(`events/authkey/uid/${uid}/history/details`);
    return response.data;
  };

  // Fast tier: header/progress tracker/instruction panel/FormData. No event scan.
  const getTransactionHistoryCore = async (uid: string) => {
    const response = await axios.get(`events/authkey/uid/${uid}/history/core`);
    return response.data;
  };

  // Full-history, lightweight tier: AI Verification Summary, Assigned Driver
  // Info, Driver Actions Panel — global aggregates, not paginated.
  const getTransactionHistorySummary = async (uid: string) => {
    const response = await axios.get(`events/authkey/uid/${uid}/history/summary`);
    return response.data;
  };

  // Paginated tier for the scrollable event-card timeline. `before` fetches an
  // older page (scroll-down "load more"); `after` fetches newer-than (realtime
  // top-up). Omit both for the initial page.
  const getTransactionHistoryTimeline = async (
    uid: string,
    options: { before?: string; after?: string; limit?: number } = {}
  ) => {
    const params: Record<string, string> = {};
    if (options.before) params.before = options.before;
    if (options.after) params.after = options.after;
    if (options.limit) params.limit = String(options.limit);
    const response = await axios.get(`events/authkey/uid/${uid}/history/timeline`, { params });
    return response.data;
  };
  const uploadEventData = async (payload: any) => {
    const response = await axios.post('events/upload', payload);
    return response.data;
  };

  const createChildTransactions = async (uid: string, payload: any) => {
    const response = await axios.post(`events/authkey/uid/${uid}/create-childrens`, payload);
    return response.data;
  };

  //for deleting the transaction
  const deleteTransaction = async (transactionKey: string) => {
    const payload = {
      key: transactionKey
    };
    const response = await axios.put(`authkey/delete`, payload);
    return response.data;
  }

  // image, file, or any other asset upload function

  const getClientsList = async () => {
    const response = await axios.get('client/list');
    return response.data;
  };

  const getClientByUid = async (uid: string) => {
    const response = await axios.get(`client/uid/${uid}/details`);
    return response.data;
  };

  

  const getTeamByUid = async (uid: string) => {
    const response = await axios.get(`team/uid/${uid}/details`);
    return response.data;
  };

  const manageTeamLabels = async (uid: string, payload: any) => {
    const response = await axios.put(`team/manage/${uid}/label`, payload);
    return response.data;
  };

  const getRolesWithFilter = async (filter: any) => {
    const response = await axios.post(`role/list`, filter);
    return response.data;
  };

  const inviteUser = async (payload: any) => {
    let email = base64Encode(payload.email);
    let phone = base64Encode(payload.phone);
    payload = { ...payload, email, phone }
    const response = await axios.post(`user/invite`, payload);
    return response.data;
  };

   const editUser = async (uid: string, payload: any) => {
    const response = await axios.put(`/user/edit/${uid}`, payload);
    return response.data;
  };

  const activateOrDeactivate = async (payload: any, uid: string) => {
    const response = await axios.put(`account/manage/uid/${uid}`, payload);
    return response.data;
  };

  const getUsers = async (payload: any) => {
    let { page, size, keyword, search, client, role, team } = payload;

    const response = await axios.get(`user`, {
      params: {
        ...(page != undefined ? { page } : {}),
        ...(size != undefined ? { size } : {}),
        ...(keyword ? { keyword } : {}),
        ...(search ? { search } : {}),
        ...(team ? { team } : {}),
        ...(client ? { client } : {}),
        ...(role ? { role } : {})
      }
    });
    return response.data;
  };

  const getUsersByClient = async (clientUid: string) => {
    const response = await axios.get(`client/uid/${clientUid}/users`);
    return response.data;
  };

  const getTeamTags = async (teamUid: string, audience?: string) => {
    const response = await axios.get(`team/uid/${teamUid}/tag`, {
      params: {
        ...(audience != undefined && audience != '' && audience != null ? { audience } : {})
      }
    });
    return response.data;
  };

  const getTeams = async (payload: { page: number; size: number; keyword?: string; clients?: string[] }) => {
    const response = await axios.post('team/list', payload);
    return response.data;
  };

  // Template leftover (no route uses it). It used to copy {email, password} into
  // localStorage; credentials are never stored in the browser.
  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    await axios.post('/api/account/register', {
      id: chance.bb_pin(),
      email,
      password,
      firstName,
      lastName
    });
  };

  const createTag = async (teamUid: string, payload: any) => {
    const response = await axios.post(`team/uid/${teamUid}/tag`, payload);
    return response.data;
  };

  const manageTag = async (teamUid: string, tagUid: string, payload: any) => {
    const response = await axios.put(`team/uid/${teamUid}/tag/${tagUid}`, payload);
    return response.data;
  };

  const manageTeamUsers = async (teamUid: string, payload: any) => {
    const response = await axios.put(`team/manage/${teamUid}/users`, payload);
    return response.data;
  };

  const getTeamUsers = async (teamUid: string) => {
    const response = await axios.get(`team/manage/${teamUid}/users`);
    return response.data;
  };

  const removeTeamUser = async (teamUid: string, userUid: string) => {
    const response = await axios.delete(`team/manage/${teamUid}/user/${userUid}`);
    return response.data;
  };

  const answerCall = async (uid: string, eventStatus: CallStatus) => {
    const response = await axios.put(`events/uid/${uid}/accept`, { eventStatus });
    return response.data;
  };

  // Dashboard → app call initiation. Returns { uid, key, keyUid, from, fromName,
  // recipients, channelName, session, callDirection } which the caller writes to
  // Firebase via FirebaseContext.addCallData (see useInitiateCall).
  const initiateCallToUser = async (
    transactionUid: string,
    opts?: { stepUid?: string; targetUserUids?: string[]; sessionType?: string }
  ) => {
    const response = await axios.post('events/call/initiate', { transactionUid, ...(opts || {}) });
    return response.data;
  };

  // Cancel an outgoing (unanswered/timed-out) call: frees the collision guard and
  // sends a cancel push so any ringing device dismisses its CallKit UI.
  const cancelCall = async (uid: string) => {
    const response = await axios.put(`events/call/${uid}/cancel`, {});
    return response.data;
  };

  // Callable users for a transaction — populates the "pick who to call" dialog.
  const getCallCandidates = async (transactionUid: string) => {
    const response = await axios.get(`events/call/${transactionUid}/candidates`);
    return response.data;
  };

  const uploadScreenshot = async (uid: string, screenshot: any) => {
    const response = await axios.put(`events/uid/${uid}/screenshot`, { screenshot });
    return response.data;
  };

  const startSessionRecording = async (uid: string, payload: { uid: string; path: string }) => {
    const response = await axios.post(`events/uid/${uid}/recording/start`, payload);
    return response.data;
  };

  const stopSessionRecording = async (uid: string, payload: { uid: string }) => {
    const response = await axios.post(`events/uid/${uid}/recording/stop`, payload);
    return response.data;
  };

  const endSession = async (uid: string) => {
    const response = await axios.put(`events/uid/${uid}/session/end`, {});
    return response.data;
  };

  const updateSessionUserName = async (uid: string, userName: string) => {
    const response = await axios.put(`events/uid/${uid}/name`, { userName });
    return response.data;
  };
  const supportReview = async (uid: string, payload: { rating: number; comment: string }) => {
    const response = await axios.put(`events/uid/${uid}/support/review`, payload);
    return response.data;
  };

  const getSupportUsers = async (uid: string) => {
    const response = await axios.get(`events/uid/${uid}/support/users`);
    return response.data;
  };

  const inviteSupportUser = async (uid: string, userUid: string) => {
    const response = await axios.get(`events/uid/${uid}/invite/user/${userUid}`);
    return response.data;
  };

  const getDashboardCount = async (payload: { start: string; end: string }) => {
    const response = await axios.get('dashboard/count', { params: payload });
    return response.data;
  };

  const getDashboardCallLogs = async (payload: { start: string; end: string; status: CallStatus }) => {
    const response = await axios.get('dashboard/call', { params: payload });
    return response.data;
  };

  const getFlowByTeamUid = async (uid: string) => {
    const response = await axios.get(`flow/team/${uid}`);
    return response.data;
  };

  // The bulk-import column/value mapping remembered for a process, so
  // importing the same shape of file again doesn't mean redoing the mapping.
  // One mapping per process — saving replaces the previous one.
  const getImportMapping = async (teamUid: string) => {
    const response = await axios.get(`flow/team/${teamUid}/import-mapping`);
    return response.data;
  };

  const saveImportMapping = async (teamUid: string, payload: WireImportMapping) => {
    const response = await axios.put(`flow/team/${teamUid}/import-mapping`, payload);
    return response.data;
  };

  const replicateTeamByUid = async (
    teamUid: string,
    payload: { name: string; newTeamName?: string; teamName?: string }
  ) => {
    const response = await axios.post(`team/uid/${teamUid}/replicate`, payload);
    return response.data;
  };

  const getClientRoles = async (clientUid: string) => {
    const response = await axios.get(`role/client/${clientUid}/roles`);
    return response.data;
  };

  const assignRoleToClient = async (clientUid: string, payload: any) => {
    const response = await axios.post(`/role`, { ...payload, clientId: clientUid });
    return response.data;
  };

  const updateClientRole = async (clientUid: string, roleUid: string, payload: any) => {
    const response = await axios.put(`/role/${roleUid}`, { ...payload, clientId: clientUid });
    return response.data;
  };

  const getPermissions = async () => {
    const response = await axios.get('/role/permissions');
    return response.data;
  };
  const completeProfile = async (data: any) => {
    let password = base64Encode(data.password);
    let newPassword = base64Encode(data.newPassword);
    data = { ...data, password, newPassword }
    const response = await axios.put('/account/complete', data);
    return response.data;
  };

  const registerFCMToken = async (payload: any) => {
    const response = await axios.post('/user/fcm-token', payload);
    return response.data;
  };

  const getUserRoleAll = async () => {
    try {
      const response = await axios.get('/role');
      return response.data;
    } catch (error) {
      console.error('Error fetching user roles:', error);
      return null;
    }
  };

  const addNewStep = async (
    teamUid: string,
    payload: {
      currentNode: {
        title: string;
        stepType: string;
        parents: string[];
        dependency: string[];
      };
      parentNode: {
        nodeKey: 'onCompleteStep' | 'onFailedStep';
      };
    }
  ) => {
    const response = await axios.post(`/flow/team/${teamUid}/step`, payload);
    // return response.data.data;
    return response.data;
  };

  // Add step and connect between existing nodes
  const addStepAndConnect = async (
    teamUid: string,
    payload: {
      currentNode: {
        title: string;
        stepType: string;
        position: { x: number; y: number };
        parents: string[];
        dependency: string[];
      };
      parentNode: {
        nodeKey: 'onCompleteStep' | 'onFailedStep';
      };
      insertBetween: Array<{
        parentStepUid: string;
        childStepUid: string;
        edgeType: 'onCompleteStep' | 'onFailedStep';
      }>;
      updatedBy?: string;
      notificationConfig?: any;
    }
  ) => {
    const response = await axios.post(`/flow/team/${teamUid}/step/connect`, payload);
    return response.data;
  };

  // Updating the step/Node
  const updateFlowDiagram = async (
    teamUid: string,
    payload: {
      steps: {
        id: string;
        parents: string[];
        onCompleteStep: string[];
        onFailedStep: string[];
        stepType: string;
        reviewers: null;
        actor: null;
        dependency: string[];
        diagramData: null;
        onInProgress: object;
        onComplete: object;
        onFailure: object;
      }[];
      diagramData: {
        id: string;
        position: { x: number; y: number };
        measured?: any;
        type?: string;
      }[];
    }
  ) => {
    const response = await axios.put(`/flow/team/${teamUid}`, payload);
    return response.data;
  };


  //delete the step 

  const deleteStepByStepUid = async (teamUid: string, stepUid: string) => {
    const response = await axios.delete(`/flow/team/${teamUid}/step/${stepUid}/reconnect`);
    return response.data;
  };

  //  adding new step flow  ( fortransaction page )

  const addNewStepFlow = async (teamUid: string) => {
    const response = await axios.get(`/flow/team/${teamUid}/new`);
    return response.data;
  };

  //  creating rule for step
  const createRuleByStep = async (stepUid: string, rulePayload: any) => {
    const response = await axios.post(`/rule-engine/step/${stepUid}`, rulePayload);
    return response.data;
  };

  //  updating rule
  const updateRule = async (ruleUid: string, rulePayload: any) => {
    const response = await axios.put(`/rule-engine/rule/${ruleUid}`, rulePayload);
    return response.data;
  };

  //  getting rules by step
  const getRulesByStep = async (stepUid: string) => {
    const response = await axios.get(`/rule-engine/step/${stepUid}/rules`);
    return response.data;
  };

  //  getting all rules
  const getAllRules = async () => {
    const response = await axios.get(`/rule-engine/rules/all`);
    return response.data;
  };

  //  getting rule by UID
  const getRuleByUid = async (ruleUid: string) => {
    const response = await axios.get(`/rule-engine/rule/${ruleUid}`);
    return response.data;
  };

  //  getting rules by team UID
  const getRulesByTeamUid = async (teamUid: string, isStepRule?: boolean) => {
    const queryParams = isStepRule ? '?isStepRule=true' : '';
    const response = await axios.get(`/rule-engine/team/${teamUid}/rules${queryParams}`);
    return response.data;
  };

  //  deleting rule by UID
  const deleteRule = async (ruleUid: string) => {
    const response = await axios.delete(`/rule-engine/rule/${ruleUid}`);
    return response.data;
  };

  //  getting Node Status
  const getNodeStatus = async (teamUid: any) => {
    const response = await axios.get(`flow/status/team/${teamUid}`);

    return response.data;
  };
  // creating  Node Status

  const createNodeStatus = async (payload: { name: string }, teamUid: string) => {
    const response = await axios.post(`flow/status/team/${teamUid}`, payload);
    return response.data;
  };

  const updateNodeStatus = async (payload: { name: string }, statusUid: string): Promise<any> => {
    const response = await axios.put(`flow/status/uid/${statusUid}`, payload);
    return response.data;
  };

  //  getting Node Reports
  const getNodeReports = async (teamUid: any) => {
    const response = await axios.get(`flow/report/team/${teamUid}`);
    return response.data;
  };

  // creating Node Report
  const createNodeReport = async (payload: { name: string }, teamUid: string) => {
    const response = await axios.post(`flow/report/team/${teamUid}`, payload);
    return response.data;
  };

  const updateNodeReport = async (payload: { name: string }, reportUid: string): Promise<any> => {
    const response = await axios.put(`flow/report/uid/${reportUid}`, payload);
    return response.data;
  };

  const deleteNodeReport = async (reportUid: string) => {
    const response = await axios.delete(`flow/report/uid/${reportUid}`);
    return response.data;
  };

  const transactionWithFilter = async (payload: any) => {
    const response = await axios.post('events/transactions', payload);
    return response.data;
  };

  const transactionOrder = async (payload: any, options?: { signal?: AbortSignal }) => {
    const response = await axios.post('authkey/keys', payload, { signal: options?.signal });
    return response.data;
  };

  const transactionKanban = async (payload: any, options?: { signal?: AbortSignal }) => {
    const response = await axios.post('authkey/kanban', payload, { signal: options?.signal });
    return response.data;
  };

  const transactionOrderWithHistory = async (payload: any) => {
    const response = await axios.post('authkey/keys-with-history', payload);
    return response.data;
  };

  const getTransactionHistory = async (uid: string) => {
    const response = await axios.get(`events/authkey/uid/${uid}/history`);
    return response.data;
  };

  const getTransactionDetails = async (uid: string) => {
    const response = await axios.get(`events/uid/${uid}/details`);
    console.log(response, 'getTransactionHistoryDetails');
    return response.data;
  };

  const getUnmappedTransaction = async (payload: any) => {
    const response = await axios.post(`events/unmapped`, payload);
    return response.data;
  };

  const getDashboardTags = async () => {
    const response = await axios.get('team/tags/dashboard');
    return response.data;
  };

  // Fetch documents for any entity
  const getDocuments = async (entityId: string, entityType: string) => {
    const response = await axios.get(`/document/${entityType}/${entityId}`);
    return response.data?.data || [];
  };

  const deleteDocument = async (documentId: string) => {
    const response = await axios.delete(`/document/${documentId}`);
    return response.data;
  };

  const uploadDocument = async (file: File, entityId: string, entityType: string) => {
    // Step 1: Upload the file to storage using uploadAsset
    const assetResult = await uploadAsset(file, `documents/${entityType}/${entityId}`);
    // Step 2: Create a document entry in the database
    const response = await axios.post(`/document/${entityType}/${entityId}`, assetResult.data);
    return response.data;
  };



  // Sign out of this session everywhere: on the server, in this browser (tokens, user
  // data, Microsoft and Firebase sign-ins) and in every other open tab.
  const logout = async (reason: SessionEndReason = 'logout') => {
    if (reason === 'logout') showToast('Successfully signed out', 'success');
    await endBrowserSession(reason);
    dispatch({
      type: LOGOUT
    });
    // Reload to update authentication state
    window.location.reload();
  };
  // Reset password
  const resetPassword = async (email: string,domainUrl:string) => {
    // Create axios instance without auth header for public endpoint
    const publicAxios = axiosBase.create({
      baseURL: axios.defaults.baseURL,
      timeout: axios.defaults.timeout
    });
    const response = await publicAxios.post('/account/forgot-password', { email,domainUrl });
    return response.data;
  };

  // Reset password with token
  const resetPasswordWithToken = async (token: string, password: string) => {
    const encodedPassword = base64Encode(password);
    // Create axios instance without auth header for public endpoint
    const publicAxios = axiosBase.create({
      baseURL: axios.defaults.baseURL,
      timeout: axios.defaults.timeout
    });
    const response = await publicAxios.post('/account/reset-password', { 
      token, 
      password: encodedPassword 
    });
    return response.data;
  };

  // Update profile function
  const updateProfile = () => {
    // TODO: Implement profile update logic
  };

  // ==============================|| RESOURCES MANAGEMENT APIs ||============================== //

  // Resource Types Management
  const getResourceTypes = async (clientUID?: string, category?: any) => {
    const params = clientUID ? { clientUID, category } : { category };
    const response = await axios.get('resource/resource-types', { params });
    return response.data;
  };

  const createResourceType = async (payload: {
    name: string;
    description: string;
    fields: Array<{
      name: string;
      type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
      required: boolean;
      dropdownSource?: 'clients' | 'teams' | 'countryCode';
      dropdownOptions?: string[];
      // `image` fields only — which camera the mobile app opens.
      cameraFacing?: 'env' | 'user';
      verification?: { enabled: boolean; provider?: string; documentType?: string } | null;
    }>;
    clientUID?: string;
    category?: 'user' | 'others';
    access?: string[];
    invite?: boolean;
    permissions?: string[];
    editable?: boolean;
    deletable?: boolean;
    allowRating?: boolean;
  }) => {
    const response = await axios.post('resource/resource-types', payload);
    return response.data;
  };

  const updateResourceType = async (id: string, payload: {
    name: string;
    description: string;
    fields: Array<{
      name: string;
      type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
      required: boolean;
      dropdownSource?: 'clients' | 'teams' | 'countryCode';
      dropdownOptions?: string[];
      // `image` fields only — which camera the mobile app opens.
      cameraFacing?: 'env' | 'user';
      verification?: { enabled: boolean; provider?: string; documentType?: string } | null;
    }>;
    clientUID?: string;
    category?: 'user' | 'others';
    access?: string[];
    invite?: boolean;
    permissions?: string[];
    editable?: boolean;
    deletable?: boolean;
    allowRating?: boolean;
  }) => {
    const response = await axios.put(`resource/resource-types/${id}`, payload);
    return response.data;
  };

  const deleteResourceType = async (id: string) => {
    const response = await axios.delete(`resource/resource-types/${id}`);
    return response.data;
  };

  // Resource Data Management
  const getResourceData = async (resourceTypeId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const response = await axios.get(`resource/resource-data/${resourceTypeId}`, { params });
    return response.data;
  };

  const getAllResourceData = async (params?: {
    resourceTypeId?: string;
    page?: number;
    limit?: number;
    search?: string;
    clientUID?: string;
  }, options?: { signal?: AbortSignal }) => {
    const response = await axios.get('resource/resource-data', { params, signal: options?.signal });
    return response.data;
  };

  const createResourceData = async (resourceTypeId: string, data: { [key: string]: any }, additionalPayload?: any) => {
    const basePayload = {
      resourceTypeId,
      data
    };
    
    // If additional payload is provided (for user resource types), merge it
    const finalPayload = additionalPayload ? { ...basePayload, ...additionalPayload } : basePayload;
    
    const response = await axios.post('resource/resource-data', finalPayload);
    return response.data;
  };

  const updateResourceData = async (id: string, payload: {
    resourceTypeId: string;
    data: { [key: string]: any };
  }, additionalPayload?: any) => {
    // If additional payload is provided (for user resource types), merge it
    const finalPayload = additionalPayload ? { ...payload, ...additionalPayload } : payload;
    
    const response = await axios.put(`resource/resource-data/${id}`, finalPayload);
    return response.data;
  };

  const deleteResourceData = async (id: string) => {
    const response = await axios.delete(`resource/resource-data/${id}`);
    return response.data;
  };

  // Resource Search API for form components
  const searchResources = async (
    resourceType: string | null,
    query: string,
    resourceTypes?: string[],
    clientUID?: string
  ) => {
    const params: any = { query };

    if (resourceTypes && resourceTypes.length > 0) {
      params.types = resourceTypes.join(',');
    } else if (resourceType) {
      params.type = resourceType;
    }

    if (clientUID) {
      params.clientUID = clientUID;
    }

    const response = await axios.get(`resource/search`, { params });
    return response.data;
  };

  // Get specific resource type by ID for form creation
  const getResourceTypeById = async (resourceTypeId: string) => {
    const response = await axios.get(`resource/resource-types/${resourceTypeId}`);
    return response.data;
  };

  // Microsoft OAuth methods
  const microsoftLogin = async (consents?: LoginConsents) => {
    setMicrosoftLoading(true);

    try {
      // Get theme config from context instead of API call
      console.log('Theme config for Microsoft OAuth:', themeConfig);

      let clientId: string | undefined;
      let tenantId: string | undefined;

      if (themeConfig?.microsoftAuth) {
        clientId = themeConfig.microsoftAuth.clientId;
        tenantId = themeConfig.microsoftAuth.tenantId;
      }

      // Initiate Microsoft OAuth flow with dynamic configuration
      const { userProfile, accessToken, account } = await completeMicrosoftOAuthFlow(clientId, tenantId);

      const payload = {
        microsoftAccessToken: accessToken,
        userProfile: userProfile,
        microsoftAccount: {
          homeAccountId: account.homeAccountId,
          environment: account.environment,
          tenantId: account.tenantId,
          username: account.username,
          localAccountId: account.localAccountId
        },
        platform: 'DASHBOARD',
        ...(consents && Object.keys(consents).length ? { consents } : {})
      };
      // Send to backend for token exchange
      let response;
      try {
        response = await axios.post('account/login', { strategy: LoginType.ENTRA_ID, payload });
      } catch (error: any) {
        throw consentRequiredFromResponse(error) ?? error;
      }

      const result = response.data;

      console.log('Microsoft OAuth response:', result);

      if (!result.success || !result.data) {
        throw new Error(result.message || 'Authentication failed');
      }

      const { token, refreshToken, firebaseToken, user } = result.data;

      if (!token) {
        throw new Error('No authentication token received');
      }

      // Set login details with correct data structure (stores the tokens for this tab)
      setLoginDetails({
        token,
        refreshToken,
        accessToken: token, // For backward compatibility
        firebaseToken,
        user
      });

      showToast('Successfully signed in with Microsoft', 'success');
      
      // Reload to redirect to dashboard
      window.location.reload();
    } catch (error: any) {
      // A consent 428 is not an access failure — the login page shows the checkboxes.
      if (error?.code !== 'CONSENT_REQUIRED') {
        showToast('User does not have access to the Application', 'error');
      }
      throw error;
    } finally {
      setMicrosoftLoading(false);
    }
  };

   const getTeamNotifications = async (teamUid: string) => {
    const response = await axios.get(`/team/uid/${teamUid}/notifications`);
    return response.data;
  };

  const getMicrosoftAccount = () => {
    return getCurrentMicrosoftAccount();
  };

  const isMicrosoftAuthenticated = () => {
    return isMicrosoftLoggedIn() && state.isLoggedIn;
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  // Get resource data by ID
  const getResourceDataById = async (resourceDataId: string) => {
    const response = await axios.get(`resource/resource-data/by-id/${resourceDataId}`);
    return response.data;
  };

  const getResourceDataByUid = async (resourceDataUid: string) => {
    const response = await axios.get(`resource/resource-data/by-uid/${resourceDataUid}`);
    return response.data;
  };

  // Batch last-login lookup for user resources (gated server-side to app-permitted
  // resource types). Returns { [uid]: { lastLoginAt, lastLogoutAt, lastLoginStrategy } }.
  const getLastLoginBatch = async (uids: string[]) => {
    const response = await axios.post('resource/resource-data/last-login', { uids });
    return response.data;
  };

  // Batch Jumio verification lookup for the people named on a page. Returns
  // { [uid|_id]: { applicable, state, verified, ... } } — see
  // components/JumioVerificationBadge.tsx for the state vocabulary.
  const getVerificationStatusBatch = async (uids: string[]) => {
    const response = await axios.post('resource/resource-data/verification-status', { uids });
    return response.data;
  };

  // Resource Rating APIs
  const getResourceTransactionsWithRatings = async (resourceId: string) => {
    const response = await axios.get(`resource/resource-data/${resourceId}/transactions-with-ratings`);
    return response.data;
  };

  const rateResource = async (payload: {
    resourceId: string;
    transactionId: string;
    rating: 'good' | 'bad' | 'none';
    comment?: string;
    userId: string;
  }) => {
    const response = await axios.post('resource/ratings', payload);
    return response.data;
  };

  const updateTransactionHistoryStep = async (historyId: string, payload: any) => {
    try {
      const response = await axios.put(`events/history/update/${historyId}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const bulkUpdateEvents = async (payload: any) => {
    try {
      const response = await axios.put('events/bulk-update', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getAllPendingCommunication = async (payload: any, options?: { signal?: AbortSignal }) => {
    try{
        const response = await axios.post('authkey/pending-communication-list', payload, {
          signal: options?.signal
        });
        return response.data;
    }catch(error){
      throw error
    }
  }

  

  const redoStep = async (uid: string, stepUid: string) => {
    try {
      const response = await axios.post(`events/authkey/uid/${uid}/redo-step`, {
        stepUid: stepUid
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Super-Admin only (backend enforces): re-run the AI detection rules for a
  // step's event. eventId = the AuthkeysEvents _id shown in the step history.
  const retryAiDetection = async (eventId: string, configId?: string) => {
    try {
      const response = await axios.post(`detection/retry`, { eventId, configId });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // ── Detection rule lifecycle: validate → preview → publish ──────────────
  // None are Super-Admin-gated: whoever authors a rule tests and publishes it.

  // Which AI providers actually have keys, plus each one's model catalogue.
  const getDetectionProviders = async () => {
    const response = await axios.get(`detection/providers`);
    return response.data;
  };

  // Plain English -> a RuleSpec for the author to review. Returns a spec only,
  // never something that runs, and saves nothing.
  const draftDetectionRule = async (teamUid: string, request: string, steps: any[], resourceTypes: any[]) => {
    const response = await axios.post(`detection/team/${teamUid}/draft`, { request, steps, resourceTypes });
    return response.data;
  };

  const previewDetectionCandidates = async (teamUid: string, rule: any, limit?: number) => {
    const response = await axios.post(`detection/team/${teamUid}/preview/candidates`, { rule, limit });
    return response.data;
  };

  // Runs the rule against a real past transaction. Persists nothing except the
  // fingerprint of what was tested (drives the "Not tested" hint).
  const previewDetectionRule = async (teamUid: string, rule: any, transactionUid: string) => {
    const response = await axios.post(`detection/team/${teamUid}/preview`, { rule, transactionUid });
    return response.data;
  };

  const backtestDetectionRule = async (teamUid: string, rule: any, limit?: number) => {
    const response = await axios.post(`detection/team/${teamUid}/backtest`, { rule, limit });
    return response.data;
  };

  const publishDetectionConfig = async (teamUid: string, configUid: string) => {
    const response = await axios.post(`detection/team/${teamUid}/${configUid}/publish`);
    return response.data;
  };

  const archiveDetectionConfig = async (teamUid: string, configUid: string) => {
    const response = await axios.post(`detection/team/${teamUid}/${configUid}/archive`);
    return response.data;
  };

  // Manually resolve an INCONCLUSIVE detection result to Match ('true') or
  // No Match ('false'). Not Super-Admin-gated — resolving a stuck result is
  // ordinary process-management work. The backend rejects anything that isn't
  // inconclusive, and requires a reason.
  const resolveDetectionResult = async (
    eventId: string,
    configId: string,
    outcome: 'true' | 'false',
    reason: string,
  ) => {
    try {
      const response = await axios.post(`detection/results/${eventId}/${configId}/resolve`, {
        outcome,
        reason,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Undo a manual resolution. The undone entry stays in manualOverride.history.
  const clearDetectionResolution = async (eventId: string, configId: string) => {
    try {
      const response = await axios.delete(`detection/results/${eventId}/${configId}/resolve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Super-Admin only (backend enforces): stop live tracking for ONE driver on a
  // transaction. driverUid = the driver's tracking node key (uid).
  const stopDriverTracking = async (transactionUid: string, driverUid: string) => {
    try {
      const response = await axios.post(`events/${transactionUid}/stop-tracking/${driverUid}`, {});
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resetToStep = async (uid: string, stepUid: string) => {
    const response = await axios.post(`events/authkey/uid/${uid}/reset-to-step`, { stepUid });
    return response.data;
  };

  const resetToStepByRole = async (uid: string, stepUid: string, resourceTypeUid: string) => {
    const response = await axios.post(`events/authkey/uid/${uid}/reset-to-step-by-role`, { stepUid, resourceTypeUid });
    return response.data;
  };

 


 const createHidingField = async (teamUid:any, payload:any) => {
  const response = await axios.post(`/hiding-fields/${teamUid}`, payload);
  return response.data;
};

 const getAllHidingFields = async (teamUid:string) => {
  const response = await axios.get(`/hiding-fields/${teamUid}`);
  return response.data;
};

 const updateHidingField = async (id:any, teamUid:any, payload:any) => {
  const response = await axios.put(`/hiding-fields/${id}/${teamUid}`, payload);
  return response.data;
};

 const deleteHidingField = async (id:string) => {
  const response = await axios.delete(`/hiding-fields/${id}`);
  return response.data;
};

 const createAssignmentMessagingConfig = async (teamUid:any, payload:any) => {
  const response = await axios.post(`/assignment-messaging/${teamUid}`, payload);
  return response.data;
};

 const getAssignmentMessagingConfigs = async (teamUid:string) => {
  const response = await axios.get(`/assignment-messaging/${teamUid}`);
  return response.data;
};

 const updateAssignmentMessagingConfig = async (id:any, teamUid:any, payload:any) => {
  const response = await axios.put(`/assignment-messaging/${id}/${teamUid}`, payload);
  return response.data;
};

 const deleteAssignmentMessagingConfig = async (id:string) => {
  const response = await axios.delete(`/assignment-messaging/${id}`);
  return response.data;
};

  // ==================== CHAT API METHODS ====================

  /**
   * Create a new chat thread for a transaction
   */
  const createChatThread = async (payload: {
    transactionId: string;
    subject?: string;
    participants: string[];
    initialMessage?: string;
    initialAttachments?: any[];
    initialMentions?: any[];
  }) => {
    const response = await axios.post('chat/thread', payload);
    return response.data;
  };

  /**
   * Get all chat threads for a transaction
   */
  const getChatThreadsByTransaction = async (transactionId: string) => {
    const response = await axios.get(`chat/transaction/${transactionId}/threads`);
    return response.data;
  };

  /**
   * Get available participants for a transaction's chat
   */
  const getChatParticipants = async (transactionId: string) => {
    const response = await axios.get(`chat/transaction/${transactionId}/participants`);
    return response.data;
  };

  /**
   * Get thread details
   */
  const getChatThreadDetails = async (threadUid: string) => {
    const response = await axios.get(`chat/thread/${threadUid}`);
    return response.data;
  };

  /**
   * Send a message to a chat thread
   */
  const sendChatMessage = async (threadUid: string, payload: {
    message: string;
    messageType?: 'TEXT' | 'IMAGE' | 'FILE' | 'SYSTEM';
    attachments?: Array<{
      fileName: string;
      filePath: string;
      mimeType: string;
      publicUrl: string;
      fileSize?: number;
    }>;
  }) => {
    const response = await axios.post(`chat/thread/${threadUid}/message`, payload);
    return response.data;
  };

  /**
   * Get messages for a chat thread
   */
  const getChatMessages = async (threadUid: string, page: number = 1, limit: number = 50) => {
    const response = await axios.get(`chat/thread/${threadUid}/messages`, {
      params: { page, limit }
    });
    return response.data;
  };

  /**
   * Mark messages as read
   */
  const markChatMessagesAsRead = async (threadUid: string, payload?: {
    messageIds?: string[];
    markAll?: boolean;
  }) => {
    const response = await axios.put(`chat/thread/${threadUid}/read`, payload || { markAll: true });
    return response.data;
  };

  /**
   * Mark all accessible unread chat messages as read for current user
   */
  const markAllChatsAsRead = async () => {
    const response = await axios.put('chat/user/read-all');
    return response.data;
  };

  /**
   * Edit a chat message (text/attachments/mentions supported by API)
   */
  const editChatMessage = async (
    messageUid: string,
    payload: {
      message?: string;
      attachments?: Array<{
        fileName: string;
        filePath: string;
        mimeType: string;
        publicUrl: string;
        fileSize?: number;
      }>;
      mentions?: Array<{ uid: string; name: string }>;
    }
  ) => {
    const response = await axios.put(`chat/message/${messageUid}`, payload);
    return response.data;
  };

  /**
   * Soft-delete a chat message
   */
  const deleteChatMessage = async (messageUid: string) => {
    const response = await axios.delete(`chat/message/${messageUid}`);
    return response.data;
  };

  /**
   * Add participants to a chat thread
   */
  const addChatParticipants = async (threadUid: string, participants: string[]) => {
    const response = await axios.post(`chat/thread/${threadUid}/participants`, { participants });
    return response.data;
  };

  const removeChatParticipants = async (threadUid: string, participantUids: string[]) => {
    const response = await axios.post(`chat/thread/${threadUid}/participants/remove`, {
      participants: participantUids,
    });
    return response.data;
  };

  /**
   * Update a chat thread
   */
  const updateChatThread = async (threadUid: string, payload: {
    subject?: string;
    status?: 'ACTIVE' | 'ARCHIVED' | 'CLOSED';
  }) => {
    const response = await axios.put(`chat/thread/${threadUid}`, payload);
    return response.data;
  };

  /**
   * Archive/delete a chat thread
   */
  const archiveChatThread = async (threadUid: string) => {
    const response = await axios.delete(`chat/thread/${threadUid}`);
    return response.data;
  };

  const createIntegrationConfig=async(teamUid:string,payload:any)=>{
    const response=await axios.post( `integrations/${teamUid}`,payload);
    return response.data
  }

  const getIntegrationConfig=async(teamUid:string)=>{
    const response=await axios.get( `integrations/${teamUid}`);
    return response.data
  }

  const deleteIntegrationConfig=async(teamId:string)=>{
    const response=await axios.delete( `integrations/${teamId}`);
    return response.data
  }

  const updateIntegrationConfig=async(id:string,teamId:string,payload:any)=>{
     const response =await axios.put(`integrations/${id}/${teamId}`,payload);
     return response.data
  }

  const editTransactionDetails=async(keyId:string)=>{
    const response= await axios.get(`/events/transaction/${keyId}/completed-steps`)
    return response
  }

  // ==============================|| ANNOUNCEMENTS ||============================== //
  // A client-facing changelog of shipped features. Create/update/delete are rejected
  // server-side for anyone but a Super Admin, and every read is scoped to the caller's
  // audience, so hiding the buttons is convenience only. See getAnnouncementVisibility
  // above — it is memoized and lives near the top of the provider.

  const createAnnouncement = async (payload: CreateAnnouncementPayload) => {
    const response = await axios.post('announcement', payload);
    return response.data;
  };

  // Returns { list, total, page, size, hasMore } — newest first, for the Load more feed.
  const getAnnouncements = async (params: AnnouncementListParams = {}) => {
    const { page, size, keyword, client, type } = params;
    const response = await axios.get('announcement', {
      params: {
        ...(page != undefined ? { page } : {}),
        ...(size != undefined ? { size } : {}),
        ...(keyword ? { keyword } : {}),
        ...(client ? { client } : {}),
        // Omitted entirely for "all" — the server treats an absent param as no filter,
        // and would drop an unrecognised one anyway.
        ...(type ? { type } : {})
      }
    });
    return response.data;
  };

  // Backs the "this will reach N people" line under the audience picker. Returns
  // { count } — null when the audience has no process, since a client-wide audience has
  // no fixed membership to quote.
  //
  // Server-side this reuses the same resolution the save does, cross-tenant checks
  // included, so an audience it rejects is one the save would have rejected too.
  const getAnnouncementAudienceCount = async (params: { client?: string; process?: string; roles?: string[] }) => {
    const response = await axios.get('announcement/audience-count', {
      params: {
        ...(params.client ? { client: params.client } : {}),
        ...(params.process ? { process: params.process } : {}),
        ...(params.roles?.length ? { roles: params.roles } : {})
      }
    });
    return response.data;
  };

  // A uid outside the caller's audience reads as 404 rather than 403, so the endpoint
  // does not leak that the announcement exists.
  const getAnnouncementByUid = async (uid: string) => {
    const response = await axios.get(`announcement/uid/${uid}`);
    return response.data;
  };

  // Who has viewed one announcement — each viewer with first/last open time and open
  // count. Super Admin only server-side; returns { viewers, total }.
  const getAnnouncementViews = async (uid: string) => {
    const response = await axios.get(`announcement/uid/${uid}/views`);
    return response.data;
  };

  // Partial — send only the fields being changed. Omitting both `clients` and
  // `allClients` leaves the audience untouched.
  const updateAnnouncement = async (uid: string, payload: UpdateAnnouncementPayload) => {
    const response = await axios.put(`announcement/uid/${uid}`, payload);
    return response.data;
  };

  // Soft delete — flips status to false, the record survives.
  const deleteAnnouncement = async (uid: string) => {
    const response = await axios.delete(`announcement/uid/${uid}`);
    return response.data;
  };

  // ==============================|| LEGAL & CONSENT ||============================== //
  // Contract: PRIVACY_CONSENT_API.md. Everything under client/uid/:uid/legal* and
  // consent-report is Super Admin only server-side (see
  // getLegalAccess above); the consent/resource-data routes are scoped to the caller's
  // own client.

  // Pre-login document, resolved like utility/config. 404 when nothing is published.
  const getPublicLegalDocument = async (slug: LegalSlug, params: { domain?: string; hash?: string }) => {
    const response = await axios.get(`legal/public/${slug}`, { params });
    return response.data;
  };

  // One exact published version, by DocMeta.uid. The login checkbox links use this so
  // they always open the version whose uid is sent with the consent.
  const getPublicLegalDocumentByUid = async (uid: string) => {
    const response = await axios.get(`legal/public/uid/${encodeURIComponent(uid)}`);
    return response.data;
  };

  // The caller's own client's current document. 404 when the client hasn't published one.
  const getLegalDocument = async (slug: LegalSlug) => {
    const response = await axios.get(`legal/document/${slug}`);
    return response.data;
  };

  const getClientLegal = async (clientUid: string) => {
    const response = await axios.get(`client/uid/${clientUid}/legal`);
    return response.data;
  };

  // 400 with a "publish documents first" message when enabling privacyTerms without both
  // documents resolvable.
  const updateClientLegalConsent = async (clientUid: string, payload: LegalConsentSettings) => {
    const response = await axios.put(`client/uid/${clientUid}/legal/consent`, payload);
    return response.data;
  };

  // Documents are immutable — every publish creates version n+1.
  const publishClientLegalDocument = async (clientUid: string, slug: LegalSlug, payload: PublishLegalDocumentPayload) => {
    const response = await axios.post(`client/uid/${clientUid}/legal/documents/${slug}`, payload);
    return response.data;
  };

  const getClientLegalDocumentVersion = async (clientUid: string, slug: LegalSlug, version: number) => {
    const response = await axios.get(`client/uid/${clientUid}/legal/documents/${slug}/versions/${version}`);
    return response.data;
  };

  // Returns { rows, total, page, limit }. limit is capped at 5000 server-side.
  const getConsentReport = async (clientUid: string, params: ConsentReportParams = {}) => {
    const response = await axios.get(`client/uid/${clientUid}/consent-report`, {
      params: {
        type: params.type || 'ALL',
        status: params.status || 'ALL',
        ...(params.search ? { search: params.search } : {}),
        page: params.page ?? 1,
        limit: params.limit ?? 50
      }
    });
    return response.data;
  };

  // Returns { signedOut }. Rejects the affected users' tokens at their next refresh.
  const signOutUnconsentedUsers = async (clientUid: string) => {
    const response = await axios.post(`client/uid/${clientUid}/legal/sign-out-unconsented`);
    return response.data;
  };

  // Returns { privacyTermsEnabled, smsEnabled, current, history }.
  const getResourceConsent = async (resourceDataUid: string) => {
    const response = await axios.get(`consent/resource-data/${resourceDataUid}`);
    return response.data;
  };

  // Admin-recorded SMS consent or withdrawal (source ADMIN). 400 when the person has no
  // phone number.
  const recordResourceSmsConsent = async (resourceDataUid: string, payload: RecordSmsConsentPayload) => {
    const response = await axios.post(`consent/resource-data/${resourceDataUid}/sms`, payload);
    return response.data;
  };

  /**
   * Get or create an escalation chat thread
   * Used when accepting an escalation chat from the mobile app
   */
  const getOrCreateEscalationChatThread = async (transactionId: string, stepUid: string) => {
    const response = await axios.get(`chat/escalation/${transactionId}/${stepUid}`);
    return response.data;
  };

  /**
   * Create an escalation chat thread with initial message
   */
  const createEscalationChatThread = async (payload: {
    transactionId: string;
    stepUid: string;
    subject?: string;
    participants?: string[];
    initialMessage?: string;
    escalationData?: any;
  }) => {
    const response = await axios.post('chat/escalation', payload);
    return response.data;
  };


  const deleteNodeStatus = async (uid: string) => {
  const response = await axios.delete(`/flow/status/uid/${uid}`, {
    data: { updatedBy: 'current-user-id' } // Replace with actual user ID
  });
  return response.data;
};

  const getSignedUrl=async(payload:any)=>{
    const response=await axios.post('/authkey/file-signed-url',payload);
    return response.data;
  }

  const getTransactionAttachments = async (uid: string) => {
    const response = await axios.get(`events/authkey/uid/${uid}/attachments`);
    return response.data;
  };


  /**
   * Upload files for chat attachments
   * Returns array of attachment objects {fileName, filePath, mimeType, publicUrl, fileSize}
   */
  const uploadChatFiles = async (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await axios.post('chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };

  /**
   * Get signed URL for downloading a chat attachment
   */
  const getSignedDownloadUrl = async (filePath: string, fileName: string) => {
    const response = await axios.post('utility/file/signed/download', { file_path: filePath, file_name: fileName });
    return response.data;
  };

  // Chat Group APIs
  const getChatGroups = async (transactionId: string) => {
    const response = await axios.get(`chat/transaction/${transactionId}/groups`);
    return response.data;
  };

  const createChatGroup = async (transactionId: string, payload: { name: string; members: string[] }) => {
    const response = await axios.post(`chat/transaction/${transactionId}/groups`, payload);
    return response.data;
  };

  const updateChatGroup = async (groupUid: string, payload: { name?: string; members?: string[] }) => {
    const response = await axios.put(`chat/groups/${groupUid}`, payload);
    return response.data;
  };

  const deleteChatGroup = async (groupUid: string) => {
    const response = await axios.delete(`chat/groups/${groupUid}`);
    return response.data;
  };

  const getSuggestedChatGroups = async (transactionId: string) => {
    const response = await axios.get(`chat/transaction/${transactionId}/groups/suggested`);
    return response.data;
  };

  // Check if thread exists with exact participants
  const checkExistingChatThread = async (transactionId: string, participants: string[]) => {
    const response = await axios.post('chat/check-existing-thread', { transactionId, participants });
    return response.data;
  };

  // ==================== END CHAT API METHODS ====================

  const getAnalyticsEmbedUrl = async () => {
    const response = await axios.get('dashboard/analytics-embed-url');
    return response.data;
  };

  const getConsolidatedHxH = async (params?: { dateFrom?: string; dateTo?: string; process?: string; }) => {
    const response = await axios.get('dashboard/embed-consolidated-hxh', { params });
    return response.data;
  };

  const getTransactionEmbedUrl = async (transactionKey: string) => {
    const response = await axios.get(`dashboard/transaction-embed-url/${transactionKey}`);
    return response.data;
  };

  const getSoftwareIssuesEmbedUrl = async () => {
    const response = await axios.get('dashboard/software-issues-embed-url');
    return response.data;
  };

  const getHelpEmbedUrl = async () => {
    const response = await axios.get('dashboard/help-embed-url');
    return response.data;
  };

  // ==================== DETECTION CONFIG API METHODS ====================

  const getFlowStatusByTeam = async (teamUid: string) => {
    const response = await axios.get(`flow/status/team/${teamUid}`);
    return response.data;
  };

  const getDetectionConfig = async (teamUid: string) => {
    const response = await axios.get(`flow/team/${teamUid}/detection`);
    return response.data;
  };

  const createDetectionConfig = async (teamUid: string, payload: any) => {
    const response = await axios.post(`flow/team/${teamUid}/detection`, payload);
    return response.data;
  };

  const updateDetectionConfig = async (teamUid: string, configUid: string, payload: any) => {
    const response = await axios.put(`flow/team/${teamUid}/detection/${configUid}`, payload);
    return response.data;
  };

  const deleteDetectionConfig = async (teamUid: string, configUid: string) => {
    const response = await axios.delete(`flow/team/${teamUid}/detection/${configUid}`);
    return response.data;
  };

  // ==================== VERIFICATION CONFIG API METHODS ====================

  const getVerificationConfig = async (teamUid: string) => {
    const response = await axios.get(`flow/team/${teamUid}/verification`);
    return response.data;
  };

  const createVerificationConfig = async (teamUid: string, payload: any) => {
    const response = await axios.post(`flow/team/${teamUid}/verification`, payload);
    return response.data;
  };

  const updateVerificationConfig = async (teamUid: string, configUid: string, payload: any) => {
    const response = await axios.put(`flow/team/${teamUid}/verification/${configUid}`, payload);
    return response.data;
  };

  const deleteVerificationConfig = async (teamUid: string, configUid: string) => {
    const response = await axios.delete(`flow/team/${teamUid}/verification/${configUid}`);
    return response.data;
  };

  // ==================== DEFAULT STEPS CONFIG API METHODS ====================

  const getDefaultSteps = async (teamUid: string) => {
    const response = await axios.get(`flow/team/${teamUid}/default-steps`);
    return response.data;
  };

  const createDefaultStep = async (teamUid: string, payload: any) => {
    const response = await axios.post(`flow/team/${teamUid}/default-steps`, payload);
    return response.data;
  };

  const updateDefaultStep = async (teamUid: string, stepUid: string, payload: any) => {
    const response = await axios.put(`flow/team/${teamUid}/default-steps/${stepUid}`, payload);
    return response.data;
  };

  const deleteDefaultStep = async (teamUid: string, stepUid: string) => {
    const response = await axios.delete(`flow/team/${teamUid}/default-steps/${stepUid}`);
    return response.data;
  };

  // ==================== TRANSACTION CONTROLS (per-process action toggles) ====================

  // ==================== LOCATION TRACKING (per-process on/off) ====================

  const getLocationTracking = async (teamUid: string) => {
    const response = await axios.get(`flow/team/${teamUid}/location-tracking`);
    return response.data;
  };

  const updateLocationTracking = async (teamUid: string, payload: any) => {
    const response = await axios.put(`flow/team/${teamUid}/location-tracking`, payload);
    return response.data;
  };

  // ==================== ESCALATION DEFAULT (per-process; steps inherit) ====================

  const getEscalationDefault = async (teamUid: string) => {
    const response = await axios.get(`flow/team/${teamUid}/escalation-default`);
    return response.data;
  };

  const updateEscalationDefault = async (teamUid: string, payload: any) => {
    const response = await axios.put(`flow/team/${teamUid}/escalation-default`, payload);
    return response.data;
  };

  // ==================== TRANSACTION MONITORING API METHODS ====================

  const getMonitoringConfigs = async (teamUid: string) => {
    const response = await axios.get(`monitoring/team/${teamUid}/configs`);
    return response.data;
  };

  const createMonitoringConfig = async (teamUid: string, payload: any) => {
    const response = await axios.post(`monitoring/team/${teamUid}/configs`, payload);
    return response.data;
  };

  const updateMonitoringConfig = async (teamUid: string, configUid: string, payload: any) => {
    const response = await axios.put(`monitoring/team/${teamUid}/configs/${configUid}`, payload);
    return response.data;
  };

  const deleteMonitoringConfig = async (teamUid: string, configUid: string) => {
    const response = await axios.delete(`monitoring/team/${teamUid}/configs/${configUid}`);
    return response.data;
  };

  const getTransactionAlerts = async (transactionUid: string) => {
    const response = await axios.get(`monitoring/transaction/${transactionUid}/alerts`);
    return response.data;
  };

  // Paginated history for ONE monitoring rule on a transaction. Backs "Load older"
  // inside an expanded alert group, where getTransactionAlerts' cap runs out.
  const getTransactionRuleAlerts = async (
    transactionUid: string,
    configUid: string,
    skip = 0,
    limit = 20
  ) => {
    const response = await axios.get(
      `monitoring/transaction/${transactionUid}/alerts/${configUid}?skip=${skip}&limit=${limit}`
    );
    return response.data;
  };

  const getTeamAlerts = async (teamUid: string) => {
    const response = await axios.get(`monitoring/team/${teamUid}/alerts`);
    return response.data;
  };

  // ==================== RESOURCE IMAGE API METHODS ====================

  const uploadResourceImage = async (resourceId: string, fieldName: string, formData: FormData) => {
    const response = await axios.post(`resource/resource-data/${resourceId}/image/${fieldName}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };

  const getResourceImage = async (resourceId: string, fieldName: string) => {
    const response = await axios.get(`resource/resource-data/${resourceId}/image/${fieldName}`);
    return response.data;
  };


  const checkDeleteDependencies = async (
    teamUid: string,
    options: { fieldUid?: string; stepUid?: string }
  ) => {
    const response = await axios.get(`team/uid/${teamUid}/dependency-check`, {
      params: {
        ...(options?.fieldUid ? { fieldUid: options.fieldUid } : {}),
        ...(options?.stepUid ? { stepUid: options.stepUid } : {})
      }
    });
    return response.data;
  }

  // ==================== DETECTION TARGETS (on-device capture classes) ========
  // A "target" is a class the mobile capture gate can recognise — driving
  // licence, number plate, face, and whatever is added next. Its label vectors
  // live on the server precisely so adding or re-tuning a class is an upload
  // rather than an app release. Reads are open to any signed-in user; writes are
  // Super Admin only (enforced server side).

  const getDetectionTargets = async () => {
    const response = await axios.get('detection-target');
    return response.data;
  };

  const getDetectionTarget = async (name: string) => {
    const response = await axios.get(`detection-target/${name}`);
    return response.data;
  };

  /** Body is exactly what export_mobileclip.py writes, plus the model stamp. */
  const saveDetectionTarget = async (name: string, payload: any) => {
    const response = await axios.put(`detection-target/${name}`, payload);
    return response.data;
  };

  const deleteDetectionTarget = async (name: string) => {
    const response = await axios.delete(`detection-target/${name}`);
    return response.data;
  };

  // ==================== END DETECTION & IMAGE API METHODS ====================

  return (
    <ApiContext.Provider
      value={{
        ...state,
        getDetectionTargets,
        getDetectionTarget,
        saveDetectionTarget,
        deleteDetectionTarget,
        getPriorityTransactions,
        getTeamNotifications,
        saveTeamNotifications,
        deleteTeamNotification,
        updateTeamNotification,
        base64Decode,
        login,
        logout,
        register,
        resetPassword,
        resetPasswordWithToken,
        updateProfile,
        uploadAsset,
        getAssetSignedUrl,
        submitSSODetails,
        createClient,
        showToast,
        getClients,
        mapKeyToTeam,
        getClientByUid,
        updateClient,
        getTeamByClient,
        getRolesWithFilter,
        inviteUser,
        getTransactionHistoryDetails,
        getTransactionHistoryCore,
        getTransactionHistorySummary,
        getTransactionHistoryTimeline,
        deleteTransaction,
        updateTransactionHistoryStep,
        redoStep,
        resetToStep,
        resetToStepByRole,
        getUsers,
        activateOrDeactivate,
        getTeams,
        getClientsList,
        createTeam,
        updateTeam,
        updateChildTxnStartStep,
        getTeamByUid,
        manageTeamLabels,
        getTeamTags,
        getFormData,
        createTag,
        manageTag,
        getUsersByClient,
        manageTeamUsers,
        getTeamUsers,
        removeTeamUser,
        answerCall,
        initiateCallToUser,
        cancelCall,
        getCallCandidates,
        uploadScreenshot,
        startSessionRecording,
        stopSessionRecording,
        endSession,
        updateSessionUserName,
        supportReview,
        getSupportUsers,
        inviteSupportUser,
        editUser,
        getDashboardCount,
        getDashboardCallLogs,
        transactionWithFilter,
        transactionOrder,
        transactionKanban,
        transactionOrderWithHistory,
        getTransactionHistory,
        getTransactionDetails,
        getUnmappedTransaction,
        getDashboardTags,
        getFlowByTeamUid,
        getImportMapping,
        saveImportMapping,
        replicateTeamByUid,
        addNewStep,
        addStepAndConnect,
        addNewStepFlow,
        uploadDocument,
        getDocuments,
        deleteDocument,
        updateFlowDiagram,
        deleteStepByStepUid,
        // Rule Engine APIs
        createRuleByStep,
        updateRule,
        getRulesByStep,
        getAllRules,
        getRuleByUid,
        getRulesByTeamUid,
        deleteRule,
        getSSODetails,
        microsoftLogin,
        getMicrosoftAccount,
        isMicrosoftAuthenticated,
        microsoftLoading,
        getUserRoleAll,
        getClientRoles,
        assignRoleToClient,
        updateClientRole,
        getPermissions,        
        getNodeStatus,
        createNodeStatus,
        updateNodeStatus,
        getNodeReports,
        createNodeReport,
        updateNodeReport,
        deleteNodeReport,
        getTeamDetailsByUid,
        completeProfile,
        registerFCMToken,
        uploadEventData,
        bulkUpdateEvents,
        // Resources Management APIs
        getResourceDataByUid,
        getLastLoginBatch,
        getVerificationStatusBatch,
        retryAiDetection,
        resolveDetectionResult,
        clearDetectionResolution,
        getDetectionProviders,
        draftDetectionRule,
        previewDetectionCandidates,
        previewDetectionRule,
        backtestDetectionRule,
        publishDetectionConfig,
        archiveDetectionConfig,
        stopDriverTracking,
        getResourceTypes,
        createResourceType,
        updateResourceType,
        deleteResourceType,
        getResourceData,
        getAllResourceData,
        createResourceData,
        updateResourceData,
        deleteResourceData,
        searchResources,
        getResourceTypeById,
        getResourceDataById,
        getResourceTransactionsWithRatings,
        rateResource,
        updateNotificationStatus,
        createHidingField,
        getAllHidingFields,
        updateHidingField,
        deleteHidingField,
        createAssignmentMessagingConfig,
        getAssignmentMessagingConfigs,
        updateAssignmentMessagingConfig,
        deleteAssignmentMessagingConfig,
        createChatThread,
        getChatThreadsByTransaction,
        getChatParticipants,
        getChatThreadDetails,
        sendChatMessage,
        getChatMessages,
        markChatMessagesAsRead,
        markAllChatsAsRead,
        editChatMessage,
        deleteChatMessage,
        addChatParticipants,
        removeChatParticipants,
        updateChatThread,
        archiveChatThread,
        createIntegrationConfig,
        getIntegrationConfig,
        deleteIntegrationConfig,
        updateIntegrationConfig,
        getOrCreateEscalationChatThread,
        createEscalationChatThread,
        uploadChatFiles,
        getSignedDownloadUrl,
        getChatGroups,
        createChatGroup,
        updateChatGroup,
        deleteChatGroup,
        getSuggestedChatGroups,
        getUserChatThreads,
        checkExistingChatThread,
        getAllPendingCommunication,
        deleteNodeStatus,
        editTransactionDetails,
        getSignedUrl,
        getTransactionAttachments,
        getAnalyticsEmbedUrl,
        getTransactionEmbedUrl,
        getSoftwareIssuesEmbedUrl,
        getHelpEmbedUrl,
        createChildTransactions,
        getConsolidatedHxH,
        // Detection Config APIs
        getFlowStatusByTeam,
        getDetectionConfig,
        getLocationTracking,
        getEscalationDefault,
        updateEscalationDefault,
        updateLocationTracking,
        createDetectionConfig,
        updateDetectionConfig,
        deleteDetectionConfig,
        // Verification Config APIs
        getVerificationConfig,
        createVerificationConfig,
        updateVerificationConfig,
        deleteVerificationConfig,
        // Default Steps Config APIs
        getDefaultSteps,
        createDefaultStep,
        updateDefaultStep,
        deleteDefaultStep,
        // Resource Image APIs
        uploadResourceImage,
        getResourceImage,
        // Transaction Monitoring APIs
        getMonitoringConfigs,
        createMonitoringConfig,
        updateMonitoringConfig,
        deleteMonitoringConfig,
        getTransactionAlerts,
        getTransactionRuleAlerts,
        getTeamAlerts,
        checkDeleteDependencies,
        // Announcements APIs
        createAnnouncement,
        getAnnouncements,
        getAnnouncementAudienceCount,
        getAnnouncementByUid,
        getAnnouncementViews,
        updateAnnouncement,
        deleteAnnouncement,
        getAnnouncementVisibility,
        markAnnouncementRead,
        markAllAnnouncementsRead,
        // Legal & Consent APIs
        getLegalAccess,
        getPublicLegalDocument,
        getPublicLegalDocumentByUid,
        getLegalDocument,
        getClientLegal,
        updateClientLegalConsent,
        publishClientLegalDocument,
        getClientLegalDocumentVersion,
        getConsentReport,
        signOutUnconsentedUsers,
        getResourceConsent,
        recordResourceSmsConsent
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export default ApiContext;
